import React, { useState, memo } from 'react';
import { ComposableMap, Geographies, Geography, Marker, Line, ZoomableGroup, Sphere, Graticule } from 'react-simple-maps';
import { MapPin, X, Maximize2, Minimize2, Building2 } from 'lucide-react';
import { SERVICE_LOCATIONS, SERVED_COUNTRIES, HUB_CONNECTIONS } from '../mock/geoLocations';
import { useLang } from '../contexts/LanguageContext';

const WORLD_TOPO = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const US_STATES_TOPO = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const RealWorldMap = ({ compact = false }) => {
  const { t } = useLang();
  const [hovered, setHovered] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [zoom, setZoom] = useState({ coordinates: [20, 15], zoom: 1 });
  const [showStates, setShowStates] = useState(true);

  const filteredLocs = selectedRegion
    ? SERVICE_LOCATIONS.filter(l => l.region === selectedRegion)
    : SERVICE_LOCATIONS;

  const regionAccent = (r) => (r === 'americas' || r === 'oceania' ? '#f97316' : '#94a3b8');

  return (
    <section className="relative py-20 bg-gradient-to-b from-[#0e1116] via-[#12151a] to-[#0e1116] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:50px_50px]"></div>
      <div className="relative max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <div className="text-green-500 font-bold text-[12px] uppercase tracking-[0.25em] mb-3">{t('globalReach')}</div>
          <h2 className="text-4xl md:text-5xl font-black">Real Geographic <span className="text-orange-500">Coverage</span></h2>
          <p className="text-gray-400 mt-4">{t('servingWorldwide')} — actual lat/lng of every project hub plotted on a real-boundary world map with country & US-state outlines.</p>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {[
            { id: null, name: 'All Regions' },
            { id: 'americas', name: 'Americas' },
            { id: 'europe', name: 'Europe' },
            { id: 'asia', name: 'Asia' },
            { id: 'oceania', name: 'Oceania' }
          ].map(r => (
            <button key={r.name} onClick={() => setSelectedRegion(r.id)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-bold uppercase tracking-wider transition-colors ${selectedRegion === r.id ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-900/40 text-gray-300 hover:border-green-500'}`}>
              {r.id && <span className="w-2 h-2 rounded-full" style={{ background: regionAccent(r.id) }} />}
              {r.name}
            </button>
          ))}
          <button onClick={() => setShowStates(!showStates)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-bold uppercase tracking-wider transition-colors ${showStates ? 'bg-orange-500/20 border-orange-500 text-orange-300' : 'border-gray-900/40 text-gray-400 hover:border-orange-500'}`}>
            <Building2 className="w-3.5 h-3.5" /> US States
          </button>
        </div>

        {/* Map */}
        <div className="relative aspect-[2/1] bg-[#04080a] border border-gray-900/50 rounded-lg overflow-hidden">
          {/* SVG patterns / definitions for stipple/dot fills */}
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <pattern id="countryDots" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.45" fill="#64748b" opacity="0.55" />
              </pattern>
              <pattern id="countryDotsHi" x="0" y="0" width="3" height="3" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.55" fill="#94a3b8" opacity="0.85" />
              </pattern>
              <pattern id="countryDotsAccent" x="0" y="0" width="3" height="3" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.55" fill="#f97316" opacity="0.85" />
              </pattern>
            </defs>
          </svg>

          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 145, center: [10, 18] }}
            width={1400}
            height={700}
            style={{ width: '100%', height: '100%', background: '#04080a' }}
          >
            <ZoomableGroup
              center={zoom.coordinates}
              zoom={zoom.zoom}
              onMoveEnd={(p) => setZoom(p)}
              minZoom={1}
              maxZoom={6}
            >
              {/* Background graticule (meridians/parallels) */}
              <Sphere stroke="#2a2f38" strokeWidth={0.4} fill="transparent" />
              <Graticule stroke="#191d24" strokeWidth={0.2} />

              {/* World countries with dotted/stippled fill */}
              <Geographies geography={WORLD_TOPO}>
                {({ geographies }) =>
                  geographies.map(geo => {
                    const name = geo.properties && geo.properties.name;
                    const isServed = SERVED_COUNTRIES.has(name);
                    const fill = isServed ? 'url(#countryDotsHi)' : 'url(#countryDots)';
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke={isServed ? '#94a3b8' : '#64748b'}
                        strokeWidth={isServed ? 0.45 : 0.25}
                        style={{
                          default: { outline: 'none' },
                          hover: { fill: 'url(#countryDotsAccent)', outline: 'none', cursor: 'pointer' },
                          pressed: { outline: 'none' }
                        }}
                        onMouseEnter={() => setHovered({ name, isServed })}
                        onMouseLeave={() => setHovered(null)}
                      />
                    );
                  })
                }
              </Geographies>

              {/* US States (admin-1 boundaries) */}
              {showStates && (
                <Geographies geography={US_STATES_TOPO}>
                  {({ geographies }) =>
                    geographies.map(geo => (
                      <Geography
                        key={geo.rsmKey + '_state'}
                        geography={geo}
                        fill="transparent"
                        stroke="#f97316"
                        strokeWidth={0.25}
                        strokeOpacity={0.6}
                        strokeDasharray="1,1"
                        style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }}
                      />
                    ))
                  }
                </Geographies>
              )}

              {/* Hub connection arcs */}
              {HUB_CONNECTIONS.map((c, i) => (
                <Line
                  key={c.name}
                  from={c.from}
                  to={c.to}
                  stroke="#f97316"
                  strokeWidth={0.45}
                  strokeOpacity={0.45}
                  strokeDasharray="3,2"
                />
              ))}

              {/* Service location markers */}
              {filteredLocs.map(loc => {
                const color = regionAccent(loc.region);
                return (
                  <Marker key={loc.name} coordinates={loc.coords}>
                    <g onMouseEnter={() => setHovered({ ...loc, isLocation: true })} onMouseLeave={() => setHovered(null)} style={{ cursor: 'pointer' }}>
                      {/* outer pulse */}
                      <circle r={loc.hub ? 9 : 5} fill={color} opacity={0.18}>
                        <animate attributeName="r" values={loc.hub ? '6;12;6' : '3;7;3'} dur="2.4s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.6;0;0.6" dur="2.4s" repeatCount="indefinite" />
                      </circle>
                      <circle r={loc.hub ? 3.5 : 2} fill={color} stroke="#ffffff" strokeWidth={loc.hub ? 0.9 : 0.5} />
                      {loc.hub && (
                        <text textAnchor="middle" y={-7} style={{ fontFamily: 'system-ui', fontSize: 4.2, fontWeight: 800, fill: '#ffffff' }}>
                          {loc.country.split(' ')[0].toUpperCase()}
                        </text>
                      )}
                    </g>
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>

          {/* Zoom controls */}
          <div className="absolute top-3 right-3 flex flex-col gap-1.5">
            <button onClick={() => setZoom({ ...zoom, zoom: Math.min(6, zoom.zoom * 1.4) })} className="w-9 h-9 bg-[#0e1116]/90 hover:bg-gray-700 border border-gray-700/50 rounded text-white flex items-center justify-center transition-colors" aria-label="Zoom in"><Maximize2 className="w-4 h-4" /></button>
            <button onClick={() => setZoom({ ...zoom, zoom: Math.max(1, zoom.zoom / 1.4) })} className="w-9 h-9 bg-[#0e1116]/90 hover:bg-gray-700 border border-gray-700/50 rounded text-white flex items-center justify-center transition-colors" aria-label="Zoom out"><Minimize2 className="w-4 h-4" /></button>
            <button onClick={() => setZoom({ coordinates: [20, 15], zoom: 1 })} className="w-9 h-9 bg-orange-500 hover:bg-orange-600 border border-orange-400 rounded text-white text-[10px] font-bold flex items-center justify-center transition-colors" aria-label="Reset">R</button>
          </div>

          {/* Legend */}
          <div className="absolute bottom-3 left-3 bg-[#0e1116]/90 backdrop-blur-md border border-gray-700/50 rounded-lg px-4 py-3 text-[11px]">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>Americas / Oceania</div>
              <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gray-500"></span>Europe / Asia</div>
              <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-500 ring-2 ring-white/40"></span>HQ / Hub</div>
            </div>
            <div className="mt-1.5 text-gray-500 text-[10px]">Dotted countries are served · dashed orange = US states · dashed arcs = hub connections</div>
          </div>

          {/* Hover info */}
          {hovered && (
            <div className="absolute top-3 left-3 bg-[#0e1116]/95 border border-gray-700/60 rounded-lg p-3 backdrop-blur-md shadow-xl max-w-xs">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-orange-400" />
                    <h4 className="font-bold text-sm">{hovered.name}</h4>
                    {hovered.hub && <span className="text-[9px] bg-orange-500 text-white font-bold px-1.5 py-0.5 rounded uppercase">HUB</span>}
                  </div>
                  {hovered.country && <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">{hovered.country} · {hovered.region}</div>}
                  {hovered.coords && <div className="text-[10px] text-green-400 font-mono mt-1">{hovered.coords[1].toFixed(3)}°, {hovered.coords[0].toFixed(3)}°</div>}
                  {hovered.isLocation === undefined && (
                    <div className="text-[10px] text-gray-500 mt-1">{hovered.isServed ? '✓ Country served' : 'Hover to explore'}</div>
                  )}
                </div>
                <button onClick={() => setHovered(null)} className="text-gray-500 hover:text-white" aria-label="close"><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          )}
        </div>

        {/* Stats strip */}
        {!compact && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Service Locations', value: SERVICE_LOCATIONS.length, color: 'green' },
              { label: 'Hub Cities', value: SERVICE_LOCATIONS.filter(l => l.hub).length, color: 'orange' },
              { label: 'Countries Served', value: SERVED_COUNTRIES.size, color: 'green' },
              { label: 'Hub Connections', value: HUB_CONNECTIONS.length, color: 'orange' }
            ].map(s => (
              <div key={s.label} className="bg-[#1a1f2e] border border-gray-900/40 rounded p-4 text-center">
                <div className={`text-3xl font-black ${s.color === 'green' ? 'text-green-400' : 'text-orange-400'}`}>{s.value}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default memo(RealWorldMap);
